import * as Yup from 'yup';
import { v4 as uuidV4 } from 'uuid';
import { IFarm } from './farm';
import { IFarmFormValues } from './farmFormValues';
import { IFarmUpsert } from './farmUpsert';
import { IOrganization } from '../organization/organization';
import { IFtpDetail } from '../ftpDetail/ftpDetail';
import { IContactInfo } from '../contactInfo/contactInfo';
import { IAccreditation } from '../accreditation/accreditation';
import OptionType from '../../optionType';
import { IOptionType } from '@zz2/zz2-ui';
import ValidationHelper from '../../../../services/helper/validationHelper';
import { isNullOrWhiteSpace } from '../../../../services/appFunctionsService';

export default class FarmModelHelper {
    public static createFormValues = (
        farm : IFarm | null,
        organizations : Array<IOrganization>,
        ftpDetails : Array<IFtpDetail>,
        contactInfos : Array<IContactInfo>,
        accreditations : Array<IAccreditation>,
    ) : IFarmFormValues => {
        if (!farm) return {
            id: 0,
            guid: uuidV4(),
            code: '',
            name: '',
            globalGapNumber: '',
            address: '',
            telNumber: '',
            faxNumber: '',
            bankAccount: '',
            bankName: '',
            extendedDescription: '',
            vatNumber: '',
            exportedCode: '',
            exportedDescription: '',
            accreditation: null,
            organizations: [],
            ftpDetails: [],
            contactInfos: [],
            isActive: true,
        };

        const accreditation = accreditations.find((x) => x.id === farm.accreditationId);
        const organizationOptions = organizations
            .filter((x) => farm.organizationIds.includes(x.id))
            .map((x) => { // TODO: OptionType.fromDataModel
                return { label: `(${x.code}) ${x.name}`, value: x.id };
            });
        const ftpDetailOptions = ftpDetails
            .filter((x) => farm.ftpDetailIds.includes(x.id))
            .map((x) => {
                return { label: x.name, value: x.id };
            });
        const contactInfoOptions = contactInfos
            .filter((x) => farm.contactInfoIds.includes(x.id))
            .map((x) => {
                return { label: x.name, value: x.id };
            });


        return {
            id: farm.id,
            guid: farm.guid,
            code: farm.code,
            name: farm.name,
            globalGapNumber: farm.globalGapNumber,
            address: farm.address,
            telNumber: farm.telNumber,
            faxNumber: farm.faxNumber,
            bankAccount: farm.bankAccount,
            bankName: farm.bankName,
            extendedDescription: farm.extendedDescription,
            vatNumber: farm.vat,
            exportedCode: farm.exportedCode,
            exportedDescription: farm.exportedDescription,
            accreditation: accreditation
                ? OptionType.fromDataModel(accreditation)
                : null,
            organizations: organizationOptions,
            ftpDetails: ftpDetailOptions,
            contactInfos: contactInfoOptions,
            isActive: farm.isActive,
        };
    };

    public static createUpsert = (formValues : IFarmFormValues) : IFarmUpsert => {
        return {
            id: formValues.id,
            guid: formValues.guid,
            code: formValues.code,
            name: formValues.name,
            globalGapNumber: !isNullOrWhiteSpace(formValues.globalGapNumber)
                ? formValues.globalGapNumber
                : null,
            address: !isNullOrWhiteSpace(formValues.address)
                ? formValues.address
                : null,
            telNumber: !isNullOrWhiteSpace(formValues.telNumber)
                ? formValues.telNumber
                : null,
            faxNumber: !isNullOrWhiteSpace(formValues.faxNumber)
                ? formValues.faxNumber
                : null,
            bankAccount: !isNullOrWhiteSpace(formValues.bankAccount)
                ? formValues.bankAccount
                : null,
            bankName: !isNullOrWhiteSpace(formValues.bankName)
                ? formValues.bankName
                : null,
            extendedDescription: !isNullOrWhiteSpace(formValues.extendedDescription)
                ? formValues.extendedDescription
                : null,
            vatNumber: !isNullOrWhiteSpace(formValues.vatNumber)
                ? formValues.vatNumber
                : null,
            exportedCode: !isNullOrWhiteSpace(formValues.exportedCode)
                ? formValues.exportedCode
                : null,
            exportedDescription: !isNullOrWhiteSpace(formValues.exportedDescription)
                ? formValues.exportedDescription
                : null,
            accreditationId: formValues.accreditation
                ? Number(formValues.accreditation.value)
                : null,
            organizationIds: formValues.organizations.map((x) => Number(x.value)),
            ftpDetailIds: formValues.ftpDetails.map((x) => Number(x.value)),
            contactInfoIds: formValues.contactInfos.map((x) => Number(x.value)),
            isActive: formValues.isActive,
        };
    };

    public static formSchema = () : Yup.ObjectSchema<IFarmFormValues> => Yup.object().shape({
        id: Yup.number().typeError('Please enter a number.').required('Required'),
        guid: Yup.string().required('Required'),
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        name: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        globalGapNumber: Yup.string().trim().defined().nullable(),
        address: Yup.string().trim().defined().nullable(),
        telNumber: Yup.string().trim().defined().nullable()
            .test('Valid Phone Number', 'Invalid Phone Number', (value) => {
                if (!value) return true;
                return ValidationHelper.isValidPhoneNumber(value);
            }),
        faxNumber: Yup.string().trim().defined().nullable()
            .test('Valid Fax Number', 'Invalid Fax Number', (value) => {
                if (!value) return true;
                return ValidationHelper.isValidFaxNumber(value);
            }),
        bankAccount: Yup.string().trim().defined().nullable(),
        bankName: Yup.string().trim().defined().nullable(),
        extendedDescription: Yup.string().trim().defined().nullable(),
        vatNumber: Yup.string().trim().defined().nullable(),
        exportedCode: Yup.string().trim().defined().nullable(),
        exportedDescription: Yup.string().trim().defined().nullable(),
        accreditation: Yup.mixed<IOptionType>().defined().nullable(),
        organizations: Yup.array<IOptionType>().defined().nullable().required('Required'),
        ftpDetails: Yup.array<IOptionType>().defined().nullable().required('Required'),
        contactInfos: Yup.array<IOptionType>().defined().nullable().required('Required'),
        isActive: Yup.boolean().required('Required'),
    });
}
